import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Input,
    Button,
    Modal,
    Dropdown,
    Row,
    Col,
    message,
    Descriptions,
    Card,
} from 'antd';
import { MapTrifold, PencilSimpleLine } from '@phosphor-icons/react';
import { EllipsisOutlined } from '@ant-design/icons';
import { aneelService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import SatelliteMap from '../MapComponent/SatelliteMap';

// ── Mapeamento de campos API → label em português ──────────────────────────
const FIELD_LABELS = {
    _id:                           '#',
    DatGeracaoConjuntoDados:       'Data Geração Dados',
    IdeNucleoCEG:                  'Núcleo CEG',
    CodCEG:                        'Código CEG',
    NomEmpreendimento:             'Empreendimento',
    NomAgente:                     'Agente',
    NumCPFCNPJ:                    'CPF/CNPJ',
    SigUFPrincipal:                'UF',
    SigTipoGeracao:                'Tipo Geração',
    DscFaseUsina:                  'Fase',
    DscRegimeExploracao:           'Regime Exploração',
    DscOrigemCombustivel:          'Origem Combustível',
    DscFonteCombustivel:           'Fonte Combustível',
    NomFonteCombustivel:           'Nome Combustível',
    DscTipoOutorga:                'Tipo Outorga',
    DatEntradaOperacao:            'Entrada Operação',
    DatInicioVigencia:             'Início Vigência',
    DatFimVigencia:                'Fim Vigência',
    MdaPotenciaOutorgadaKw:        'Pot. Outorgada (kW)',
    MdaPotenciaFiscalizadaKw:      'Pot. Fiscalizada (kW)',
    MdaGarantiaFisicaKw:           'Garantia Física (kW)',
    PctParticipacao:               'Part. (%)',
    DscPropriRegimePariticipacao:  'Regime de Participação',
    IdcGeracaoQualificada:         'Ger. Qualificada',
    NumCoordNEmpreendimento:       'Latitude',
    NumCoordEEmpreendimento:       'Longitude',
    DscSubBacia:                   'Sub-Bacia',
    DscMuninicpios:                'Município(s)',
};

// Ordem preferida das colunas (reflete layout do site original)
const PREFERRED_COLUMN_ORDER = [
    'IdeNucleoCEG',
    'NomEmpreendimento',
    'CodCEG',
    'SigUFPrincipal',
    'SigTipoGeracao',
    'DscFaseUsina',
    'DscRegimeExploracao',
    'NomAgente',
    'NumCPFCNPJ',
    'DscOrigemCombustivel',
    'DscFonteCombustivel',
    'NomFonteCombustivel',
    'DscTipoOutorga',
    'DatEntradaOperacao',
    'DatInicioVigencia',
    'DatFimVigencia',
    'MdaPotenciaOutorgadaKw',
    'MdaPotenciaFiscalizadaKw',
    'MdaGarantiaFisicaKw',
    'PctParticipacao',
    'DscPropriRegimePariticipacao',
    'IdcGeracaoQualificada',
    'NumCoordNEmpreendimento',
    'NumCoordEEmpreendimento',
    'DscSubBacia',
    'DscMuninicpios',
    'DatGeracaoConjuntoDados',
    '_id',
];

// Formatar número no padrão brasileiro (ex: 100,00)
const formatBrNumber = (val) => {
    if (val == null || val === '') return '-';
    const num = typeof val === 'number' ? val : parseFloat(String(val).replace(',', '.'));
    if (isNaN(num)) return String(val);
    return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Colunas com formatação específica
const COLUMN_CONFIG = {
    PctParticipacao:          { align: 'right',  render: (val) => formatBrNumber(val) },
    MdaPotenciaOutorgadaKw:   { align: 'right',  render: (val) => formatBrNumber(val) },
    MdaPotenciaFiscalizadaKw: { align: 'right',  render: (val) => formatBrNumber(val) },
    MdaGarantiaFisicaKw:      { align: 'right',  render: (val) => formatBrNumber(val) },
    IdeNucleoCEG:             { align: 'left' },
    _id:                      { align: 'right' },
    NumCoordNEmpreendimento:  {
        align: 'right',
        render: (val) => val != null && val !== '' ? String(val).replace(',', '.') : '-',
    },
    NumCoordEEmpreendimento:  {
        align: 'right',
        render: (val) => val != null && val !== '' ? String(val).replace(',', '.') : '-',
    },
};

const AneelDashboard = () => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
    const [searchText, setSearchText] = useState('');
    const [isMapModalVisible, setIsMapModalVisible] = useState(false);
    const [isCadastrarModalVisible, setIsCadastrarModalVisible] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState({ latitude: null, longitude: null });
    const [selectedRecord, setSelectedRecord] = useState(null);
    const { user } = useAuth();

    const roles = user?.role?.split(',').map((r) => r.trim()) || [];
    const isAdmin = roles?.includes('ROLE_ADMIN') || roles?.includes('ROLE_DEV') || roles?.includes('ADMIN');

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            const { current, pageSize } = params.pagination || pagination;
            const offset = ((current || 1) - 1) * (pageSize || 5);

            const response = await aneelService.getCombinedData({
                offset,
                limit: pageSize || 5,
                q: params.search ?? searchText,
            });

            if (response?.success && response?.data) {
                const combinedData = response.data;
                const allKeys = combinedData.length
                    ? Array.from(new Set(combinedData.flatMap((r) => Object.keys(r))))
                    : [];

                const orderedKeys = [
                    ...PREFERRED_COLUMN_ORDER.filter((k) => allKeys.includes(k)),
                    ...allKeys.filter((k) => !PREFERRED_COLUMN_ORDER.includes(k)),
                ];

                const dynamicColumns = orderedKeys.map((key) => {
                    const config = COLUMN_CONFIG[key] || {};
                    const label = FIELD_LABELS[key] || key;
                    return {
                        title: label,
                        dataIndex: key,
                        key,
                        ellipsis: true,
                        align: config.align || 'left',
                        render: config.render || ((val) => (val != null && val !== '' ? String(val) : '-')),
                    };
                });

                dynamicColumns.push({
                    title: 'Ações',
                    key: 'actions',
                    fixed: 'right',
                    width: 100,
                    render: (_, record) => (
                        <Dropdown
                            menu={{
                                items: [
                                    {
                                        key: 'view-map',
                                        label: (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <MapTrifold size={16} />
                                                Ver Mapa
                                            </span>
                                        ),
                                        onClick: () =>
                                            handleOpenMap(
                                                record.NumCoordNEmpreendimento,
                                                record.NumCoordEEmpreendimento
                                            ),
                                    },
                                    ...(isAdmin
                                        ? [
                                              {
                                                  key: 'cadastrar',
                                                  label: (
                                                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                          <PencilSimpleLine size={16} />
                                                          Cadastrar
                                                      </span>
                                                  ),
                                                  onClick: () => handleCadastrar(record),
                                              },
                                          ]
                                        : []),
                                ],
                            }}
                            trigger={['click']}
                        >
                            <Button type="text" icon={<EllipsisOutlined />} />
                        </Dropdown>
                    ),
                });

                setColumns(dynamicColumns);
                setData(combinedData);
                setPagination((prev) => ({
                    ...prev,
                    current: current ?? prev.current,
                    pageSize: pageSize ?? prev.pageSize,
                    total: response.total ?? combinedData.length,
                }));
            }
        } catch (error) {
            console.error('Erro ao buscar dados ANEEL:', error);
            message.error('Erro ao carregar dados da ANEEL.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPagination((p) => ({ ...p, current: 1 }));
        fetchData({ search: value, pagination: { ...pagination, current: 1 } });
    };

    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
        fetchData({ pagination: newPagination, search: searchText });
    };

    const handleOpenMap = (latStr, lngStr) => {
        const lat = latStr != null ? parseFloat(String(latStr).replace(',', '.')) : null;
        const lng = lngStr != null ? parseFloat(String(lngStr).replace(',', '.')) : null;
        if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
            setSelectedLocation({ latitude: lat, longitude: lng });
            setIsMapModalVisible(true);
        } else {
            message.warning('Coordenadas não disponíveis para este registro.');
        }
    };

    const handleCloseMap = () => {
        setIsMapModalVisible(false);
        setSelectedLocation({ latitude: null, longitude: null });
    };

    const handleCadastrar = (record) => {
        setSelectedRecord(record);
        setIsCadastrarModalVisible(true);
    };

    const handleIrParaCadastro = () => {
        if (selectedRecord) {
            sessionStorage.setItem('aneelRecordForCadastro', JSON.stringify(selectedRecord));
            navigate('/cadastros', { state: { fromAneel: selectedRecord } });
            setIsCadastrarModalVisible(false);
            setSelectedRecord(null);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const mainFields = [
        { key: 'NomEmpreendimento', label: 'Nome do Empreendimento' },
        { key: 'CodCEG', label: 'Código CEG' },
        { key: 'SigTipoGeracao', label: 'Tipo de Geração' },
        { key: 'DscFaseUsina', label: 'Fase da Usina' },
        { key: 'MdaPotenciaOutorgadaKw', label: 'Potência Outorgada (kW)' },
        { key: 'NumCoordNEmpreendimento', label: 'Latitude' },
        { key: 'NumCoordEEmpreendimento', label: 'Longitude' },
        { key: 'NomAgente', label: 'Agente' },
        { key: 'NumCPFCNPJ', label: 'CPF/CNPJ' },
    ];

    return (
        <div style={{ width: '100%', background: '#fff' }}>
            <Row justify="space-between" align="middle" style={{ marginTop: 20, marginBottom: 16 }}>
                <Col>
                    <h2 style={{ color: '#2957A4', margin: 0 }}>
                        Dados das APIs | SIGA e Agentes de Geração de Energia Elétrica
                    </h2>
                </Col>
                <Col>
                    <Input.Search
                        placeholder="Pesquisar na tabela..."
                        allowClear
                        enterButton="Filtrar"
                        onSearch={handleSearch}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 300 }}
                    />
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={data}
                loading={loading}
                rowKey={(record) => record.CodCEG || record._id || Math.random()}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    pageSizeOptions: ['5', '10', '20', '50'],
                    showTotal: false,
                }}
                onChange={handleTableChange}
                scroll={{ x: 'max-content' }}
                size="middle"
                bordered={false}
                style={{ background: '#fff' }}
                rowClassName={(_, index) => (index % 2 === 1 ? 'aneel-table-row-alt' : '')}
            />

            <Modal
                title="Localização no Mapa"
                open={isMapModalVisible}
                onCancel={handleCloseMap}
                footer={null}
                width={800}
            >
                {selectedLocation?.latitude != null && selectedLocation?.longitude != null ? (
                    <SatelliteMap
                        lat={selectedLocation.latitude}
                        lng={selectedLocation.longitude}
                        height={500}
                    />
                ) : (
                    <p>Coordenadas não disponíveis.</p>
                )}
            </Modal>

            <Modal
                title="Cadastrar Site a partir dos dados ANEEL"
                open={isCadastrarModalVisible}
                onCancel={() => {
                    setIsCadastrarModalVisible(false);
                    setSelectedRecord(null);
                }}
                footer={[
                    <Button key="cancel" onClick={() => setIsCadastrarModalVisible(false)}>
                        Cancelar
                    </Button>,
                    <Button key="cadastrar" type="primary" onClick={handleIrParaCadastro}>
                        Ir para Cadastro de Site
                    </Button>,
                ]}
                width={640}
            >
                {selectedRecord && (
                    <Card size="small">
                        <Descriptions column={1} size="small" bordered>
                            {mainFields.map(({ key, label }) => (
                                <Descriptions.Item key={key} label={label}>
                                    {selectedRecord[key] != null ? String(selectedRecord[key]) : '-'}
                                </Descriptions.Item>
                            ))}
                        </Descriptions>
                        <p style={{ marginTop: 16, color: '#666', fontSize: 12 }}>
                            Ao clicar em &quot;Ir para Cadastro de Site&quot;, você será redirecionado para a página de
                            cadastros com os dados da ANEEL disponíveis para preenchimento.
                        </p>
                    </Card>
                )}
            </Modal>
        </div>
    );
};

export default AneelDashboard;
